/* iter-p0-security · Public appeal page.
 *
 * Route: `/appeal`
 * Users hit this after being auto-suspended (auth flow surfaces a link
 * with `?u=<user_id>` prefill from the 403 body if we choose to expose
 * it later). The form is deliberately dumb: user_id + email + reason,
 * rate-limited server-side, deduped by user_id.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ShieldAlert, Loader2, CheckCircle2 } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function AppealPage() {
  const [params] = useSearchParams();
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const u = params.get('u');
    if (u) setUserId(u);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!userId.trim() || !email.trim() || (reason || '').trim().length < 20) {
      setError('Please fill every required field. Reason must be at least 20 characters.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/appeals`, {
        user_id: userId.trim(),
        email:   email.trim(),
        contact_phone: phone.trim() || null,
        reason:  reason.trim(),
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-lg p-6 text-center" data-testid="appeal-success">
        <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-600" />
        <h1 className="text-2xl font-semibold">Appeal received</h1>
        <p className="mt-3 text-[13.5px] text-muted-foreground">
          Thank you — a member of our team will review your account within
          two business days and follow up at the email address you
          provided. If your appeal is approved, access will be restored
          the moment we act on it.
        </p>
        <p className="mt-6 text-[11px] text-muted-foreground">
          Please do not submit a second appeal — additional messages are
          added to the same ticket and reviewed together.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg p-6" data-testid="appeal-page">
      <div className="mb-4 flex items-center gap-2">
        <ShieldAlert className="h-6 w-6 text-amber-700" />
        <h1 className="text-2xl font-semibold">Appeal your account suspension</h1>
      </div>
      <p className="mb-6 text-[13px] text-muted-foreground">
        If you believe your Crown &amp; Rose account was suspended in
        error, tell us what happened. Include as much context as you
        can — automated blocks are frequently reversed on human review.
      </p>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="ap-uid" className="text-[12px]">User ID</Label>
          <Input
            id="ap-uid" value={userId} onChange={(e) => setUserId(e.target.value)}
            placeholder="user_abc123xyz"
            className="mt-1 font-mono text-[12px]"
            data-testid="appeal-user-id"
            required
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            The identifier shown on the suspension error page, or ask support if you can't find it.
          </p>
        </div>
        <div>
          <Label htmlFor="ap-em" className="text-[12px]">Contact email</Label>
          <Input
            id="ap-em" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="mt-1 text-[12px]"
            data-testid="appeal-email"
            required
          />
        </div>
        <div>
          <Label htmlFor="ap-ph" className="text-[12px]">Phone (optional)</Label>
          <Input
            id="ap-ph" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 415 555 0100"
            className="mt-1 text-[12px]"
            data-testid="appeal-phone"
          />
        </div>
        <div>
          <Label htmlFor="ap-rs" className="text-[12px]">What happened?</Label>
          <Textarea
            id="ap-rs" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Explain what you were doing, why you think the suspension was a mistake, and anything else that would help us decide."
            className="mt-1 min-h-[140px] text-[12.5px]"
            data-testid="appeal-reason"
            required
          />
          <p className="mt-1 text-[11px] text-muted-foreground">Minimum 20 characters.</p>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-[12px] text-red-800" data-testid="appeal-error">
            {error}
          </div>
        )}

        <Button
          type="submit" disabled={busy} className="w-full"
          data-testid="appeal-submit"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Submit appeal
        </Button>
      </form>
    </div>
  );
}
